import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  getStoredConsent,
  acceptAll,
  declineAll,
} from "../../Utils/cookieManager";
import CookiePreferencesModal from "./CookiePreferencesModal";
import "./CookieConsent.css";

export default function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Check consent on mount with version validation
  useEffect(() => {
    const consent = getStoredConsent();
    if (!consent) {
      // Delay banner display slightly for smooth page render
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, []);

  // Expose global opener so Footer links or settings buttons can open modal anytime
  useEffect(() => {
    window.openCookiePreferences = () => {
      setIsModalOpen(true);
    };

    const handleConsentChanged = (event) => {
      if (event?.detail) {
        setShowBanner(false);
      }
    };

    window.addEventListener("cookieConsentChanged", handleConsentChanged);
    return () => {
      window.removeEventListener("cookieConsentChanged", handleConsentChanged);
      delete window.openCookiePreferences;
    };
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setShowBanner(false);
  };

  const handleDeclineAll = () => {
    declineAll();
    setShowBanner(false);
  };

  const handlePreferencesSaved = () => {
    setShowBanner(false);
    setIsModalOpen(false);
  };

  return (
    <>
      {/* Bottom Floating Cookie Banner */}
      {showBanner && (
        <aside
          className="cookie-banner-overlay"
          aria-label="Cookie consent banner"
          role="region"
        >
          <div className="cookie-banner-container">
            <p className="cookie-banner-text">
              We use cookies to improve our site. Some cookies are necessary for
              our website and services to function properly. Other cookies are
              optional and help personalize your experience, including
              advertising and analytics. You can consent to all cookies, decline
              all optional cookies, or manage optional cookies. Without a
              selection, our default cookie settings will apply. You can change
              your preferences at any time. To learn more, check out our{" "}
              <Link to="/privacypolicy">Cookie Policy</Link>.
            </p>

            <div className="cookie-banner-actions">
              <button
                type="button"
                className="cookie-btn cookie-btn-primary"
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
              <button
                type="button"
                className="cookie-btn cookie-btn-secondary"
                onClick={handleDeclineAll}
              >
                Decline All
              </button>
              <button
                type="button"
                className="cookie-btn-link"
                onClick={() => setIsModalOpen(true)}
              >
                Manage Cookies{" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </button>
            </div>
          </div>
        </aside>
      )}

      {/* Accessible Cookie Preferences Modal */}
      <CookiePreferencesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaved={handlePreferencesSaved}
      />
    </>
  );
}
